import React, { useMemo } from 'react';

import styled from '@emotion/styled';

import { FontWeight, SkeletonLoader } from '@clutter/clean';

import { RateGroupKindEnum } from '@graphql/platform';

import {
  challengerPromoSummaryLabel,
  couponDiscountAmount,
  isStorageCoupon,
} from '@root/components/checkout/cart/challenger_promo';
import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { useProtectionPlanInfo } from '@root/components/checkout/hooks/use_protection_plan_info';
import { resolvedTaxPercent } from '@root/components/checkout/hooks/use_storage_tax_rate';
import { PLANS } from '@root/constants/pricing';
import { Commitment } from '@root/resources/types/commitment';

import { formatCurrency } from '@utils/formatters';
import { useLastDefined } from '@utils/hooks/use_last_defined';
import { extractStoragePrice } from '@utils/pricing/pricing_set';
import { PROTECTION_PLANS } from '@utils/protection_plan';

import { DisposalSummaryRow } from './disposal_summary_row';
import { useChallengerPricingRows } from './hooks/use_challenger_pricing_rows';
import {
  ChallengerTaxRates,
  challengerTaxZip,
} from './hooks/use_challenger_tax_rates';
import { CHALLENGER as T } from './tokens';

/** Matches plan selector answers under the size line (Figma 29:3865). */
const COMMITMENT_LABEL: Record<Commitment, string> = {
  [RateGroupKindEnum.Mover]: 'Month to month',
  [RateGroupKindEnum.Flexer]: '4-month minimum',
  [RateGroupKindEnum.Saver]: '8-month minimum',
};

/** Outer shell: section-bg-primary, 352px, radius 12 (Figma Booking section). */
const Shell = styled.div`
  background: ${T.color.bgPage};
  border-radius: ${T.radius.md};
  width: ${T.layout.summaryWidth};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/** Beige summary panel (section-bg-on-primary). */
const SummaryPanel = styled.div`
  background: ${T.color.summarySurface};
  border-radius: ${T.radius.md};
  padding: ${T.space[16]} ${T.space[16]} ${T.space[24]};
  width: 100%;
  box-sizing: border-box;
`;

const SummaryInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  padding: ${T.space[12]} ${T.space[12]} ${T.space[16]};
  border-bottom: 1px solid ${T.color.borderDefault};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;
  color: ${T.color.textSecondary};
`;

const CostBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[4]};
  padding: 0 ${T.space[12]};
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  color: ${T.color.textSecondary};
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${T.space[8]};
  padding: 2px 0;
  width: 100%;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${T.color.textSecondary};
`;

/** Figma 707:19326 — promo row under Pick-up fees (appointment) or Storage plan. */
const PromoLine = styled(Line)`
  color: ${T.color.textBrandDark};
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  color: ${T.color.textSecondary};
`;

/** Gradient shimmer for price cells while storage amount is unknown. */
const PriceAmount: React.FC<{ value?: number; width?: string }> = ({
  value,
  width = '52px',
}) =>
  value != null ? (
    <>{formatCurrency(value)}</>
  ) : (
    <SkeletonLoader height="20px" width={width} flexShrink={0} />
  );

type Props = {
  taxRates: ChallengerTaxRates;
};

export const BookingSummary: React.FC<Props> = ({ taxRates }) => {
  const {
    pricingSummary: pricingSummaryRaw,
    pricingSet,
    defaultPricingSummary,
    flowState: {
      values: {
        planSize,
        commitment,
        bundleKind,
        disposalSelected,
        dateScheduled,
        coupon,
        protectionPlan,
        zip,
        address,
      },
    },
  } = useStorageCheckoutContext();

  // Keep last summary while appointment quotes refetch after a plan-size slide.
  const pricingSummary =
    useLastDefined(pricingSummaryRaw) ?? defaultPricingSummary;

  const storageTaxRate = taxRates.storage;
  const protectionTaxRate = taxRates.protection;
  const protection = useProtectionPlanInfo('challenger');

  // Price from pricing set updates immediately with the slider (no quote wait).
  const storagePlanAmount = useMemo(() => {
    if (commitment && planSize && pricingSet) {
      return extractStoragePrice(commitment, planSize, pricingSet);
    }
    return pricingSummary?.monthlyAmount;
  }, [commitment, planSize, pricingSet, pricingSummary?.monthlyAmount]);

  const protectionPlanAmount = protection.amount;
  const protectionCoverage = protectionPlan
    ? PROTECTION_PLANS[protectionPlan].coverage
    : undefined;
  const taxZip = Boolean(challengerTaxZip(zip, address));
  const storageTaxPercent = resolvedTaxPercent(storageTaxRate, taxZip);
  const protectionTaxPercent = resolvedTaxPercent(protectionTaxRate, taxZip);
  const taxesReady = storageTaxPercent != null && protectionTaxPercent != null;
  const storagePlanTax =
    storagePlanAmount != null && taxesReady
      ? storagePlanAmount * (storageTaxPercent / 100)
      : undefined;
  const protectionPlanTax = taxesReady
    ? protectionPlanAmount * (protectionTaxPercent / 100)
    : undefined;
  const tax =
    storagePlanTax != null && protectionPlanTax != null
      ? storagePlanTax + protectionPlanTax
      : undefined;
  const fullMonthlyTotal =
    storagePlanAmount != null && tax != null
      ? storagePlanAmount + protectionPlanAmount + tax
      : undefined;
  const monthlyBaseAmount =
    storagePlanAmount != null
      ? storagePlanAmount + protectionPlanAmount
      : undefined;

  const {
    appointmentPromoDiscount,
    disposalAmount,
    gasFeeAmount,
    packingLabel,
    packingPriceDisplay,
    pickupTotalDisplay,
    scheduledArrivalAmount,
    showPackingLine,
    showPickupFees,
  } = useChallengerPricingRows({
    bundleKind,
    coupon,
    dateScheduled,
    disposalSelected,
  });

  // Figma 913:43587 — storage promos under Storage plan (before Tax);
  // appointment promos under Pick-up fees (below Gas fee).
  const storagePromoDiscountRaw =
    coupon && isStorageCoupon(coupon) && monthlyBaseAmount != null
      ? couponDiscountAmount(coupon, monthlyBaseAmount)
      : 0;
  const storagePromoDiscount =
    monthlyBaseAmount != null
      ? Math.min(storagePromoDiscountRaw, monthlyBaseAmount)
      : storagePromoDiscountRaw;

  const monthlyTotal =
    fullMonthlyTotal != null
      ? Math.max(0, fullMonthlyTotal - storagePromoDiscount)
      : undefined;

  return (
    <>
      <Shell>
        <SummaryPanel>
          <SummaryInner>
            <HeaderBlock>
              <Title>Booking summary</Title>
            </HeaderBlock>
            <CostBlock>
              <SectionLabel>Monthly storage plan</SectionLabel>
              {planSize && (
                <Line>
                  <span>
                    {PLANS[planSize].label} storage
                    {commitment && (
                      <>
                        <br />
                        {COMMITMENT_LABEL[commitment]}
                      </>
                    )}
                  </span>
                  <PriceAmount value={storagePlanAmount} />
                </Line>
              )}
              {!planSize && (
                <Line>
                  <span>Storage plan</span>
                  <PriceAmount value={storagePlanAmount} />
                </Line>
              )}
              {protectionPlanAmount > 0 && protectionCoverage && (
                <Line>
                  <span>{protectionCoverage} of protection</span>
                  <span>{formatCurrency(protectionPlanAmount)}</span>
                </Line>
              )}
              {coupon && storagePromoDiscount > 0 && (
                <PromoLine>
                  <span>{challengerPromoSummaryLabel(coupon)}</span>
                  <span>-{formatCurrency(storagePromoDiscount)}</span>
                </PromoLine>
              )}
              <Line>
                <span>Tax</span>
                <PriceAmount value={tax} width="40px" />
              </Line>
              <TotalRow>
                <PriceAmount value={monthlyTotal} width="56px" />
              </TotalRow>
            </CostBlock>
            {showPickupFees && (
              <CostBlock>
                <SectionLabel>Your initial pickup includes</SectionLabel>
                {showPackingLine && packingLabel && (
                  <Line>
                    <span>{packingLabel}</span>
                    <span>{packingPriceDisplay}</span>
                  </Line>
                )}
                {scheduledArrivalAmount > 0 && (
                  <Line>
                    <span>Scheduled arrival</span>
                    <span>{formatCurrency(scheduledArrivalAmount)}</span>
                  </Line>
                )}
                {disposalSelected && (
                  <DisposalSummaryRow amount={disposalAmount} />
                )}
                {gasFeeAmount > 0 && (
                  <Line>
                    <span>Gas fee</span>
                    <span>{formatCurrency(gasFeeAmount)}</span>
                  </Line>
                )}
                {coupon && appointmentPromoDiscount > 0 && (
                  <PromoLine>
                    <span>{challengerPromoSummaryLabel(coupon)}</span>
                    <span>-{formatCurrency(appointmentPromoDiscount)}</span>
                  </PromoLine>
                )}
                <TotalRow>{pickupTotalDisplay}</TotalRow>
              </CostBlock>
            )}
          </SummaryInner>
        </SummaryPanel>
      </Shell>
    </>
  );
};
